<template>
  <div class="h-full flex">
    <div class="w-full relative bg-white">
      <div
        ref="editorContainer"
        class="h-full w-full flex"
        :class="isExpanded ? 'items-stretch' : 'items-start'"
        @click="showLockWarning"
      >
        <FloatingMenu
          v-if="isAiSuggested && (!isExpanded || !expansionPanel)"
          placement="bottom"
          :offset="[0, 8]"
          class="h-full self-start"
        >
          <template #activator="{ show }">
            <button
              class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-3 text-gray-800 rounded-md p-2"
              :class="{
                'sticky top-27': expansionPanel?.isShown.value,
                'border border-gray-800': aiSuggestedNotReported,
                'border border-green-500': !aiSuggestedNotReported && isAiSuggested,
              }"
              :title="finalizeButtonTitle"
              @click="show"
            >
              <MinusIcon v-if="isAnswerEmpty" class="w-4" />
              <CheckIcon v-else class="w-4" />
              <SparklesIcon class="w-4" />
            </button>
          </template>
          <template #content="{ hide }">
            <div class="flex flex-col bg-white rounded-lg shadow-lg py-2">
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/accept-suggestion font-medium text-gray-900 flex items-center gap-2 disabled:opacity-50"
                :disabled="!!loadingAction"
                @click="acceptAiSuggestion(hide)"
              >
                <i-material-symbols-progress-activity
                  v-if="loadingAction === 'accept'"
                  class="size-5 animate-spin"
                />
                <CheckIcon v-else class="size-5 group-hover/accept-suggestion:text-green-600" />
                Accept AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/edit-suggestion font-medium text-gray-900 flex items-center gap-2 disabled:opacity-50"
                :disabled="!!loadingAction"
                @click="editAiSuggestion(hide)"
              >
                <i-material-symbols-progress-activity
                  v-if="loadingAction === 'edit'"
                  class="size-4 mr-1 animate-spin"
                />
                <PencilIcon v-else class="size-4 mr-1 group-hover/edit-suggestion:text-blue-600" />
                Edit AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/reject-suggestion font-medium text-gray-900 flex items-center gap-2 disabled:opacity-50"
                :disabled="!!loadingAction"
                @click="rejectAiSuggestion(hide)"
              >
                <i-material-symbols-progress-activity
                  v-if="loadingAction === 'reject'"
                  class="size-5 animate-spin"
                />
                <XMarkIcon v-else class="size-5 group-hover/reject-suggestion:text-red-600" />
                Reject AI suggestion
              </button>
            </div>
          </template>
        </FloatingMenu>
        <button
          v-else-if="!isAiSuggested && (!isExpanded || !expansionPanel)"
          class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-2 rounded-md text-gray-800 p-2 self-start"
          :class="{
            'sticky top-27': expansionPanel?.isShown.value,
            'bg-gray-800 text-white': isAnswerEmpty && isFinalized,
            'bg-green-500 text-white': !isAnswerEmpty && isFinalized,
          }"
          :disabled="loadingAction === 'finalize'"
          :title="finalizeButtonTitle"
          @click="toggleReviewItemSynthesisAttributeFinalization"
        >
          <i-material-symbols-progress-activity
            v-if="loadingAction === 'finalize'"
            class="w-4 animate-spin"
          />
          <template v-else>
            <MinusIcon v-if="isAnswerEmpty" class="w-4" />
            <CheckIcon v-else class="w-4" />
          </template>
        </button>
        <div class="flex flex-col w-full">
          <div
            v-if="!isExpanded"
            class="flex flex-wrap gap-1.5 px-2 py-2 items-center cursor-pointer"
            @click="
              () => {
                selectedAlternativeIndex = null;
                expansionPanel?.expand();
              }
            "
          >
            <div
              v-if="isAnswerEmpty && isFinalized"
              class="flex items-center p-2 border border-black rounded-lg text-sm text-gray-700 bg-gray-100"
            >
              Not reported
            </div>
            <template v-else>
              <span
                v-for="(alternative, i) in alternatives"
                :key="i"
                class="flex items-center p-2 border border-blue-800 rounded-lg text-sm font-medium text-blue-800 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                @click.stop="
                  () => {
                    selectedAlternativeIndex = i;
                    expansionPanel?.expand();
                  }
                "
              >
                {{ alternative.name || `Alternative ${i + 1}` }}
              </span>
            </template>
          </div>
          <div v-if="isExpanded" class="flex flex-col w-full">
            <div
              v-if="isAnswerEmpty && isFinalized"
              class="flex items-center p-2 ml-2 mt-2 border border-black rounded-lg text-sm text-gray-700 bg-gray-100 w-26"
            >
              Not reported
            </div>
            <MedicalAlternativesEditor
              v-else-if="isVisible"
              :study="study"
              :attribute-structure="attributeStructure"
              :disabled="isFinalized || isAiSuggested || isReviewReadOnly"
              :initial-expanded-index="selectedAlternativeIndex ?? undefined"
            />
          </div>
        </div>
      </div>
      <div class="absolute bottom-2 right-2 flex items-center gap-1">
        <EquivalentExtractionCopyMedicalAlternatives
          v-if="showEquivalentCopy && !review.isReviewReadonly.value"
          :study="study"
          @copy-medical-alternatives="handleMedicalAlternativesCopy"
        />
        <StudyQA
          v-if="!review.isReviewReadonly.value && !isFinalized"
          v-model:is-generating-answer="isGeneratingAnswer"
          :study="study"
          :attribute-structure="attributeStructure"
          :query="attributeStructure.question ?? ''"
          :is-review-readonly="review.isReviewReadonly.value"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import StudyQA from "./StudyQA.vue";
import MedicalAlternativesEditor from "./MedicalAlternativesEditor.vue";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import type { MedicalAlternativesValue } from "@core/domain/types/attributeValue.type";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { Role } from "@core/domain/types/role.type";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";
import { errorMessage } from "@app/utils/error-message";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { AuthKey } from "@app/injectionKeys";
import { SparklesIcon, CheckIcon, MinusIcon, PencilIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import EquivalentExtractionCopyMedicalAlternatives from "./EquivalentExtractionCopyMedicalAlternatives.vue";

const snackbar = useSnackbar();
const loadingAction = ref<"finalize" | "accept" | "reject" | "edit" | null>(null);
const props = withDefaults(
  defineProps<{
    attributeStructure: AttributeStructure;
    study: ReviewItem;
    isExpanded: boolean;
    isReviewReadOnly?: boolean;
    expansionPanel?: any;
    showEquivalentCopy?: boolean;
  }>(),
  {
    isReviewReadOnly: false,
    showEquivalentCopy: false,
  },
);
const isGeneratingAnswer = ref(false);
const selectedAlternativeIndex = ref<number | null>(null);
const review = injectStrict(ReviewKey);

const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

const isAnswerEmpty = computed(() => {
  const value = currentAttribute.value?.value as MedicalAlternativesValue;
  return !value?.alternatives.length;
});

const alternatives = computed(() => {
  const value = currentAttribute.value?.value as MedicalAlternativesValue;
  return value?.alternatives ?? [];
});

const isFinalized = computed(() => currentAttribute.value?.isFinalized ?? false);

const isAiSuggested = computed(() => currentAttribute.value?.isAiSuggested ?? false);

const aiSuggestedNotReported = computed(
  () => currentAttribute.value?.aiSuggestedNotReported ?? false,
);

const auth = injectStrict(AuthKey);

function showLockWarning() {
  if (!review.isReviewReadonly.value) return;
  const isAdmin = auth.user.value?.role === Role.ADMIN;
  let message = "";
  if (review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED && !review.isLocked.value) {
    message = isAdmin
      ? "The plan is unlocked, lock it to make changes."
      : "The plan is unlocked. Please contact an admin to make changes.";
  } else if (review.isLocked.value) {
    message = isAdmin
      ? "The review is locked, unlock it to make changes."
      : "The review is locked. Please contact an admin to make changes.";
  }
  if (message) {
    snackbar.show(SnackbarState.ERROR, message);
  }
}

async function toggleReviewItemSynthesisAttributeFinalization() {
  if (review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED) {
    return;
  }
  loadingAction.value = "finalize";
  try {
    if (isFinalized.value) {
      await unfinalizeReviewItemSynthesisAttribute();
    } else {
      await finalizeReviewItemSynthesisAttribute();
    }
    loadingAction.value = null;
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function acceptAiSuggestion(hide: () => void) {
  loadingAction.value = "accept";
  try {
    await review.editAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    await finalizeReviewItemSynthesisAttribute();
    loadingAction.value = null;
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function rejectAiSuggestion(hide: () => void) {
  loadingAction.value = "reject";
  try {
    await review.clearAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    loadingAction.value = null;
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function editAiSuggestion(hide: () => void) {
  loadingAction.value = "edit";
  try {
    await review.editAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    loadingAction.value = null;
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function finalizeReviewItemSynthesisAttribute() {
  try {
    await review.finalizeReviewItemSynthesisAttribute(props.study.id, props.attributeStructure.id);
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  }
}

async function unfinalizeReviewItemSynthesisAttribute() {
  try {
    await review.unfinalizeReviewItemSynthesisAttribute(
      props.study.id,
      props.attributeStructure.id,
    );
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  }
}

async function handleMedicalAlternativesCopy(value: MedicalAlternativesValue) {
  try {
    await review.saveMedicalAlternativesAttributeValue(
      props.study.id,
      props.attributeStructure.id,
      value,
    );
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  }
}

const isVisible = ref(false);
const editorContainer = useTemplateRef("editorContainer");
const finalizeButtonTitle = computed(() => {
  let message = "";
  if (isFinalized.value) {
    message = "Edit extraction";
  } else {
    message = isAnswerEmpty.value ? "Mark as not reported" : "Confirm extraction";
  }
  return message;
});
onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true;
        observer.disconnect();
      }
    },
    {
      rootMargin: "50px",
      threshold: 0.1,
    },
  );

  if (editorContainer.value) {
    observer.observe(editorContainer.value);
  }
});
</script>

<style>
.loader {
  border: 4px solid var(--color-white);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
